import {
  BoardState,
  Move,
  Square,
  Color,
  PieceType,
  VARIANT_CONFIGS,
  VARIANT_SPECS,
} from '@draughts/shared';
import { squareToPosition, positionToSquare } from '../board/square-utils.js';
import { getPieceAt, isSquareOccupied } from '../board/board-utils.js';
import {
  getManMoveDirections,
  getKingMoveDirections,
} from './directions.js';

export function getSimpleMoves(board: BoardState, square: Square): Move[] {
  const piece = getPieceAt(board, square);

  if (!piece) {
    return [];
  }

  if (piece.type !== PieceType.MAN) {
    return [];
  }

  const variantConfig = VARIANT_CONFIGS[board.variant];
  const boardSize = variantConfig.size;
  const darkSquareBottomRight = variantConfig.darkSquareBottomRight;
  const allSquares = variantConfig.playableSquares === boardSize * boardSize;
  const rules = VARIANT_SPECS[board.variant].rules;
  const moves: Move[] = [];
  const currentPos = squareToPosition(square, boardSize, darkSquareBottomRight, allSquares);
  const directions = getManMoveDirections(piece.color, rules);

  for (const dir of directions) {
    const targetPos = {
      row: currentPos.row + dir.row,
      col: currentPos.col + dir.col,
    };

    const targetSquare = positionToSquare(targetPos, boardSize, darkSquareBottomRight, allSquares);

    if (targetSquare === null) {
      continue;
    }

    if (isSquareOccupied(board, targetSquare)) {
      continue;
    }

    const isPromotion = willPromote(piece.color, targetPos.row, boardSize);

    moves.push({
      from: square,
      to: targetSquare,
      captures: [],
      isPromotion,
    });
  }

  return moves;
}

export function getKingMoves(board: BoardState, square: Square): Move[] {
  const piece = getPieceAt(board, square);

  if (!piece) {
    return [];
  }

  if (piece.type !== PieceType.KING) {
    return [];
  }

  const variantConfig = VARIANT_CONFIGS[board.variant];
  const boardSize = variantConfig.size;
  const darkSquareBottomRight = variantConfig.darkSquareBottomRight;
  const allSquares = variantConfig.playableSquares === boardSize * boardSize;
  const rules = VARIANT_SPECS[board.variant].rules;
  const moves: Move[] = [];
  const currentPos = squareToPosition(square, boardSize, darkSquareBottomRight, allSquares);
  const directions = getKingMoveDirections(rules);

  for (const dir of directions) {
    let distance = 1;

    while (true) {
      const targetPos = {
        row: currentPos.row + dir.row * distance,
        col: currentPos.col + dir.col * distance,
      };

      const targetSquare = positionToSquare(targetPos, boardSize, darkSquareBottomRight, allSquares);

      if (targetSquare === null) {
        break;
      }

      if (isSquareOccupied(board, targetSquare)) {
        break;
      }

      moves.push({
        from: square,
        to: targetSquare,
        captures: [],
        isPromotion: false,
      });

      if (!rules.flyingKings) {
        break;
      }

      distance++;
    }
  }

  return moves;
}

/**
 * WHITE: top row (1) | BLACK: bottom row (boardSize)
 */
export function willPromote(color: Color, targetRow: number, boardSize: number = 10): boolean {
  if (color === Color.WHITE) {
    return targetRow === 1;
  } else {
    return targetRow === boardSize;
  }
}
